import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useSeo } from "../lib/seo";

const SUPPORT_EMAIL = "bookfreely.support@gmail.com";
const LAST_UPDATED = "October 24, 2026";
const EFFECTIVE_DATE = "August 23, 2026";

/**
 * Terms of Service page.
 * Reflects the current architecture: Stripe Connect KYC is the sole identity
 * verification, businesses become active automatically after Stripe verifies
 * them, and human reviewers make all final enforcement decisions.
 */
export default function TermsPage() {
  useSeo({
    title: "Terms of Service — BookFreely",
    description:
      "The rules that govern how customers and businesses use BookFreely, including payments, subscriptions, and community guidelines.",
    path: "/terms",
  });
  return (
    <div className="min-h-screen" style={{ background: "var(--gf-bg)" }}>
      <Header />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10" data-testid="terms-page">
        <h1 className="font-display text-4xl mt-1" style={{ color: "var(--gf-text)" }}>Terms of Service</h1>
        <p className="text-xs mt-2" style={{ color: "var(--gf-text-muted)" }}>
          Last updated: {LAST_UPDATED} · Effective date: {EFFECTIVE_DATE}
        </p>

        <div className="prose prose-sm max-w-none mt-8 space-y-6" style={{ color: "var(--gf-text-soft)" }}>

          <Section n="1" title="About BookFreely (Marketplace Disclaimer)">
            <p>
              <b>BookFreely is an online marketplace that connects customers with independent local service providers.</b> Customers browse services, book appointments, and pay securely online; businesses manage bookings and receive payouts through Stripe Connect. BookFreely is <b>not</b> a service provider itself. All services are performed by the independent Businesses acting on their own behalf. BookFreely does not employ, supervise, direct, or control any Business or any individual providing services through the platform.
            </p>
          </Section>

          <Section n="2" title="Independent businesses">
            <p>
              Each Business is an independent third party solely responsible
              for the services it offers, including its quality, timeliness,
              safety, workmanship, licensing, insurance, and compliance with
              applicable law. BookFreely disclaims any warranty regarding the
              accuracy of Business listings, availability, credentials, or the
              outcome of any booking.
            </p>
          </Section>

          <Section n="3" title="Eligibility &amp; age requirements">
            <p>
              BookFreely enforces the following age requirements. If we discover an account has been created in violation of these rules, we will suspend or terminate it and cancel any pending bookings.
            </p>
            <ul className="list-disc pl-6">
              <li><b>Under 13</b> — Not permitted to create any BookFreely account.</li>
              <li><b>Customers 13–17</b> — May use BookFreely <b>only</b> with the permission and supervision of a parent or legal guardian. A guardian&rsquo;s full name and email must be on file at signup.</li>
              <li><b>Customers 18+</b> — May use BookFreely independently.</li>
              <li><b>Business owners 18+</b> — May independently create and operate a business account, including completing identity verification and receiving payouts.</li>
              <li><b>Business owners 13–17</b> — May operate a business account <b>only</b> under the supervision of a parent or legal guardian. The guardian&rsquo;s full name and email must be on file, and the <b>guardian</b> is legally responsible for identity verification, tax reporting, payout account ownership, and compliance with these Terms.</li>
            </ul>
            <p>
              By using BookFreely you confirm you meet these requirements. Date of birth is collected at signup (or on first sign-in for social-login accounts). Once set, only BookFreely support can update it — please contact <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: "var(--gf-accent)" }}>{SUPPORT_EMAIL}</a> for corrections.
            </p>
          </Section>

          <Section n="4" title="Customer responsibilities">
            <p>Customers agree to:</p>
            <ul className="list-disc pl-6">
              <li>Provide accurate booking information.</li>
              <li>Treat Businesses respectfully.</li>
              <li>Verify the identity of any provider arriving on-site before granting access or making payment.</li>
              <li>Resolve most service-quality issues directly with the Business.</li>
              <li>File good-faith reports through the platform when policies are violated.</li>
            </ul>
          </Section>

          <Section n="5" title="Business responsibilities">
            <p>Businesses agree to:</p>
            <ul className="list-disc pl-6">
              <li>Provide accurate profile and service information.</li>
              <li>Hold any licenses, permits, and insurance required by law.</li>
              <li>Complete <b>Stripe Connect identity verification (KYC)</b> — this is the <b>sole</b> identity verification BookFreely performs. KYC is required to <b>receive payouts</b> and to display the <b>Verified</b> badge. It is <b>not</b> required to publish services, appear in search, or accept bookings: a Business with an active subscription, in good standing, and with at least one published service is visible and bookable before KYC is complete. Until KYC is complete, the Business&rsquo;s share of each payment is held by BookFreely as a pending transfer.</li>
              <li>Perform booked services professionally, safely, and on schedule.</li>
              <li>Respond to customer messages within a reasonable time.</li>
              <li>Comply with BookFreely&rsquo;s Community Guidelines (Section 9).</li>
              <li>Accept BookFreely&rsquo;s automated payment routing, the marketplace acquisition fee model, and the customer booking &amp; processing fee described in Section 7.</li>
            </ul>
          </Section>

          <Section n="6" title="Identity verification (Stripe Connect KYC)">
            <p>
              BookFreely relies exclusively on <b>Stripe Connect</b> for
              Business identity verification. Government-issued IDs, business
              formation documents, tax information, and bank details are
              collected, verified, and held by Stripe under Stripe&rsquo;s own
              terms and privacy policy. BookFreely does not collect, store, or
              manually review any government-issued ID or other KYC document
              separately from Stripe, and there is no separate BookFreely
              approval workflow.
            </p>
            <p>
              <b>What KYC does and does not control.</b> A Business becomes
              active on BookFreely — visible in search and able to accept
              bookings — once its subscription is active, its account is in
              good standing, and it has at least one service that meets the
              publication requirements. Stripe KYC is <b>not</b> part of that
              gate. Stripe KYC controls two things: whether the Business can
              <b> receive payouts</b>, and whether the <b>Verified</b> badge is
              displayed on its listings and profile.
            </p>
            <p>
              <b>Not every listed Business is identity-verified.</b> Only
              Businesses that Stripe has approved (Stripe reports both
              <i> charges enabled</i> and <i>payouts enabled</i>) display the
              Verified badge. The absence of the badge means Stripe has not yet
              approved that Business&rsquo;s identity — it does not mean the
              Business is unable to take your booking. Customers should apply
              the same judgement they would with any independent local
              provider.
            </p>
          </Section>

          <Section n="7" title="Payments, fees &amp; subscriptions">
            <p>
              BookFreely uses Stripe Connect for all payments. Customer
              payments are processed on the BookFreely platform account and
              routed to the Business&rsquo;s connected Stripe account.
            </p>
            <p>
              <b>Marketplace acquisition fee.</b> BookFreely charges
              <b> no commission</b> on bookings from customers a Business brings
              itself (direct, QR, or link bookings) or from returning customers.
              When BookFreely&rsquo;s marketplace introduces a Business to a
              <b> brand-new customer</b>, a <b>one-time acquisition fee of 10%</b>{" "}
              of the service price, <b>capped at $30</b>, applies to that
              customer&rsquo;s <b>first booking only</b>. Every subsequent
              booking by that customer is treated as a returning customer at
              <b> 0%</b>. The acquisition status of a booking is determined at
              the time the booking is created and is not changed afterwards.
            </p>
            <p>
              <b>Customer booking &amp; processing fee.</b> Customers pay a
              one-time booking &amp; processing fee on top of the service booked
              (currently <b>3.85% of the full service price</b>, charged once at
              checkout, subject to change). A remaining balance collected later
              does not incur a second processing fee.
            </p>
            <p>
              <b>Payments before a Business completes Stripe KYC, and held
              marketplace funds.</b> Customer payments succeed normally. The
              charge is taken on the BookFreely platform account and the
              Business&rsquo;s share is recorded as <b>held as a pending
              transfer</b> until the appointment is complete and Stripe reports
              both <i>charges enabled</i> and <i>payouts enabled</i> for that
              connected account. The held amount (service price minus any
              applicable one-time acquisition fee) is then released to that
              Business&rsquo;s connected account exactly once. <b>The customer is
              never charged a second time when held funds are released</b>, and
              the customer never needs to re-book or re-pay.
            </p>
            <p>
              <b>Refunds.</b> A refund returns the customer&rsquo;s money
              regardless of where the Business&rsquo;s share currently sits. If
              the funds are still <b>held as a pending transfer</b>, the pending
              transfer is cancelled and nothing is sent to the Business. If the
              funds were <b>already released</b> to the Business&rsquo;s
              connected account, the transfer is reversed, so the
              Business&rsquo;s Stripe balance is reduced by their share. On a
              full refund any one-time acquisition fee is reversed in full; on a
              partial refund the acquisition fee is recalculated against the
              retained amount. In all cases the refunded amount comes back to
              the customer&rsquo;s original payment method.
            </p>
            <p>
              <b>Business subscription fees</b> are billed directly to
              Businesses through Stripe. Current pricing is
              <b> $19.99/month</b> (with a <b>7-day free trial</b> available
              once per business, on the Monthly plan only) or <b>$199.99/year</b>,
              billed annually. The Yearly plan is billed immediately at checkout
              and does not include a free trial. Business subscription fees are
              <b> generally non-refundable</b>, including for partial
              billing periods upon cancellation, downgrade, or account
              deletion, except in cases of billing errors or where required by
              applicable law. Refund requests may be reviewed at
              BookFreely&rsquo;s sole discretion. Cancelling a subscription
              stops future renewals but does not entitle the Business to a
              pro-rated refund of the current billing period.
            </p>
            <p className="mt-3">
              <b>Saved cards and later charges.</b> When you book, your card
              details are collected and stored by our payment provider (Stripe)
              — BookFreely never receives or stores your full card number. If a
              booking has a balance remaining after any deposit, you authorize
              BookFreely to save that card and charge the exact remaining amount
              shown to you at booking <b>automatically 24 hours before the
              appointment</b>. If you book <b>24 hours or less</b> before the
              appointment, the full applicable amount is charged at checkout
              instead and no later charge is made. The amount cannot be
              increased without you approving a
              new amount, we do not charge cancelled or declined bookings, and a
              saved card is not reused for any other purpose without separate
              authorization. If a charge is declined or needs authentication we
              set the booking to &ldquo;Payment required&rdquo; and send you a
              secure link instead of retrying your card automatically.
</p>
          </Section>

          <Section n="8" title="Prohibited activities">
            <p>
              To keep BookFreely safe and fair for everyone, users may
              <b> not</b>, directly or through others:
            </p>
            <ul className="list-disc pl-6">
              <li>Engage in fraud, scams, deceptive advertising, or any misrepresentation of identity, credentials, licensing, insurance, pricing, or availability.</li>
              <li>Impersonate any person, business, employee, or BookFreely staff, or falsely claim affiliation with any third party.</li>
              <li>Upload, transmit, or link to viruses, malware, spyware, worms, or any other malicious code or content that could harm the platform or its users.</li>
              <li>Circumvent, evade, or attempt to avoid BookFreely&rsquo;s fees, payment routing, subscription billing, or payment flows — including taking a marketplace-introduced customer off-platform to avoid the one-time acquisition fee.</li>
              <li>Attempt to gain unauthorized access to accounts, servers, databases, APIs, or restricted areas of the platform; scrape, harvest, or crawl the platform except as expressly permitted.</li>
              <li>Interfere with, disrupt, or place unreasonable load on the platform, including denial-of-service attacks, spam, mass account creation, or bot traffic.</li>
              <li>Harass, threaten, discriminate against, stalk, or endanger any user, Business, provider, or BookFreely employee.</li>
              <li>Violate any applicable federal, state, local, or international law, including consumer protection, tax, employment, health &amp; safety, licensing, and anti-money-laundering laws.</li>
              <li>Upload or share content you do not have the right to share, or that infringes intellectual property, privacy, or publicity rights.</li>
            </ul>
            <p>
              Violation of this Section may result in immediate suspension or
              termination of the offending account, cancellation of pending
              bookings, forfeiture of platform benefits, and referral to law
              enforcement where appropriate.
            </p>
          </Section>

          <Section n="9" title="Community guidelines &amp; enforcement">
            <p>
              Customers may report Businesses through the platform. Reports
              are automatically summarized and severity-scored by an AI
              moderation assistant to help human reviewers prioritize their
              queue. <b>The AI moderation assistant never issues punitive
              action on its own.</b> Every warning, strike, suspension, or
              permanent removal is reviewed and finalized by a member of the
              BookFreely team.
            </p>
            <p>Strike ladder:</p>
            <ul className="list-disc pl-6">
              <li><b>Strike 1</b> — final warning.</li>
              <li><b>Strike 2</b> — temporary suspension.</li>
              <li><b>Strike 3</b> — permanent removal from BookFreely.</li>
            </ul>
          </Section>

          <Section n="10" title="Suspension &amp; termination">
            <p>
              BookFreely may suspend, restrict, or terminate any account —
              customer or business — that violates these Terms or the
              Community Guidelines, engages in fraud, forges documents,
              threatens safety, abuses staff, or otherwise poses risk to the
              community. Such action may be taken with or without prior
              warnings, at BookFreely&rsquo;s reasonable discretion, and
              subject to applicable law. Terminated Businesses may lose
              access to their listings, upcoming bookings, and subscription
              features.
            </p>
          </Section>

          <Section n="11" title="Refund disclaimer">
            <p>
              Deposit refundability is set by each Business at the time of
              listing. BookFreely does not guarantee refunds outside the
              refund policy in effect at the time of booking. Chargebacks and
              disputes are handled through Stripe. Service-quality
              disagreements should first be resolved directly with the
              Business. Business subscription refund rules are addressed in
              Section 7.
            </p>
            <p>
              <strong>Chargeback &amp; dispute responsibility.</strong> If a
              customer&rsquo;s bank disputes a payment and the dispute is lost,
              the Business bears the cost of the net service amount it was paid
              for that booking: BookFreely reverses only the Business&rsquo;s
              net service proceeds actually transferred for that booking (never
              more than the Business received, and accounting for any prior
              refunds or partial reversals). BookFreely absorbs its own customer
              Booking &amp; Processing Fee, any retained marketplace acquisition
              fee, and the Stripe dispute fee for that booking. A reversal may
              create a negative balance on the Business&rsquo;s Stripe account,
              which Stripe recovers from the Business&rsquo;s future incoming
              funds; BookFreely does not separately charge the Business&rsquo;s
              card or bank for it. A dispute being opened does not by itself
              suspend a Business. Refunds cannot be issued while a dispute is
              open on the same payment.
            </p>
          </Section>

          <Section n="12" title="Account termination by user">
            <p>
              You may terminate your BookFreely account at any time. Business
              accounts entering deletion begin a 30-day grace period during
              which they are hidden from discovery and their subscription is
              cancelled. Booking history, payments, and dispute records are
              retained for tax, legal, Stripe, and fraud-prevention purposes
              as required by law.
            </p>
          </Section>

          <Section n="13" title="Intellectual property">
            <p>
              The BookFreely name, logo, brand, tagline (&ldquo;Book It
              Freely.&rdquo;), website, software, designs, graphics, copy,
              and all other platform content (collectively, the &ldquo;BookFreely
              Materials&rdquo;) are owned by BookFreely or its licensors and
              are protected by copyright, trademark, and other intellectual
              property laws. You may not copy, modify, distribute, sell,
              lease, reverse-engineer, or create derivative works of the
              BookFreely Materials except as expressly permitted by us in
              writing.
            </p>
            <p>
              <b>User-generated content</b> — including business listings,
              profile information, photos, videos, descriptions, reviews,
              messages, and any other content you upload or submit
              (collectively, &ldquo;User Content&rdquo;) — remains your
              property. By uploading or submitting User Content you grant
              BookFreely a <b>non-exclusive, worldwide, royalty-free,
              sublicensable license</b> to host, store, reproduce, display,
              perform, adapt (for formatting), and distribute that content as
              reasonably necessary to operate, promote, and improve the
              platform, including in marketing materials and previews of the
              platform.
            </p>
            <p>
              You represent and warrant that you own or have all necessary
              rights, licenses, consents, and permissions in your User
              Content, and that your User Content does not infringe or
              violate any third party&rsquo;s intellectual property, privacy,
              publicity, or other rights. You are solely responsible for
              your User Content and agree to indemnify BookFreely against
              claims arising from it. We may remove User Content that we
              believe, in good faith, violates these Terms or applicable
              law.
            </p>
          </Section>

          <Section n="14" title="Limitation of liability">
            <p>
              To the fullest extent permitted by applicable law, BookFreely
              (and its officers, directors, employees, and agents) shall not
              be liable for any indirect, incidental, special, consequential,
              or punitive damages, or for any loss of profits, data, use,
              goodwill, or other intangible losses, resulting from (a) your
              use of, or inability to use, the platform, (b) the conduct or
              content of any Business or third party on the platform,
              including any personal injury, property damage, workmanship
              dispute, scheduling dispute, cancellation, or other issue
              arising from services provided by independent Businesses; or
              (c) unauthorized access to or use of our servers, except where
              such liability cannot be excluded or limited under applicable
              law.
            </p>
            <p>
              BookFreely&rsquo;s total cumulative liability arising out of or
              relating to these Terms will not exceed the greater of USD $100
              or the total platform fees paid to BookFreely by you in the 12
              months preceding the claim.
            </p>
          </Section>

          <Section n="15" title="Force majeure">
            <p>
              Neither BookFreely nor its users shall be liable for any delay
              or failure to perform obligations under these Terms to the
              extent that the delay or failure is caused by events beyond
              their reasonable control, including but not limited to: natural
              disasters (fires, floods, earthquakes, storms, pandemics),
              internet or telecommunications outages, cyberattacks or other
              security incidents, government or regulatory actions,
              embargoes, war, terrorism, civil unrest, labor disputes or
              strikes, and failures, disruptions, or delays of third-party
              service providers (including Stripe, our email provider, our AI
              moderation provider, or our hosting provider). Affected
              obligations are suspended for the duration of the event, and
              affected parties will use reasonable efforts to resume
              performance as soon as practicable.
            </p>
          </Section>

          <Section n="16" title="Dispute resolution &amp; arbitration">
            <p>
              Any dispute arising out of or relating to these Terms or the
              platform shall first be attempted to be resolved through
              good-faith negotiation. If unresolved, disputes shall be
              settled by binding arbitration on an individual basis under the
              rules of a mutually agreed arbitration provider, in the
              Business&rsquo;s or Customer&rsquo;s local jurisdiction where
              required by law. Class-action waivers apply to the extent
              permitted by law. Nothing in this Section prevents either
              party from seeking injunctive or equitable relief in a court of
              competent jurisdiction for intellectual property or
              confidentiality claims.
            </p>
          </Section>

          <Section n="17" title="Governing law">
            <p>
              These Terms are governed by the laws of the <b>State of
              Georgia, United States</b>, without regard to conflict-of-laws
              principles, except where local mandatory law provides
              otherwise. Subject to Section 16 (Dispute resolution &amp;
              arbitration), the parties consent to the exclusive jurisdiction
              of the state and federal courts located in the State of Georgia
              for any matter not subject to arbitration.
            </p>
          </Section>

          <Section n="18" title="Changes to these Terms">
            <p>
              We may update these Terms from time to time to reflect changes
              to the platform, our services, our legal obligations, or for
              other operational reasons. When we make material changes, we
              will update the &ldquo;Last updated&rdquo; and &ldquo;Effective
              date&rdquo; at the top of this page and, where appropriate,
              notify users by email or through an on-platform notice before the
              changes take effect. Non-material changes (typo fixes,
              formatting, clarifications) may be made without notice. Your
              continued use of the platform after the effective date of the
              updated Terms constitutes acceptance of those changes. If you
              do not agree to the updated Terms, you must stop using
              BookFreely and may close your account.
            </p>
          </Section>

          <Section n="19" title="Privacy">
            <p>
              Please see our <a href="/privacy" style={{ color: "var(--gf-accent)" }}>Privacy Policy</a> for details on how BookFreely collects and uses information.
            </p>
          </Section>

          <Section n="20" title="Contact">
            <p>
              Questions or notices under these Terms may be sent to <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: "var(--gf-accent)" }}>{SUPPORT_EMAIL}</a>.
            </p>
          </Section>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function Section({ n, title, children }) {
  return (
    <section data-testid={`terms-section-${n}`}>
      <h2 className="font-display text-xl" style={{ color: "var(--gf-text)" }}>
        {n}. {title}
      </h2>
      <div className="mt-1 space-y-2">{children}</div>
    </section>
  );
}
